import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Box
} from '@mui/material';
import {
  Business,
  ExpandMore,
  Check,
  Add,
  Person
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../context/OrganizationContext';
import styles from '../../styles/OrganizationSelector.module.css';

const OrganizationSelector = ({ collapsed = false }) => {
  const navigate = useNavigate();
  const {
    currentOrganization,
    organizations,
    switchOrganization,
    switchToIndividualMode,
    loading,
    hasIndividualSubscription,
    isOrganizationUser,
    hasEnterpriseSubscription,
    individualSubscription
  } = useOrganization();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOrganizationSelect = (org) => {
    handleClose();

    if (org.organizationUuid === currentOrganization?.organizationUuid) {
      return; // Already selected
    }

    // Set flag to show loading overlay across reload (handled by MainPage)
    sessionStorage.setItem('accountSwitching', 'true');

    // Switch to the selected organization
    switchOrganization(org.organizationUuid);

    // Check if currently on pricing page - if so, redirect to dashboard
    const currentPath = window.location.pathname;
    if (currentPath.includes('/plans') || currentPath.includes('/pricing')) {
      window.location.href = '/dashboard';
    } else {
      // Reload to refresh all data in new context
      window.location.reload();
    }
  };

  const handleCreateNew = () => {
    handleClose();
    navigate('/signup/organization');
  };

  const handleViewIndividual = () => {
    handleClose();

    // Set flag to show loading overlay across reload (handled by MainPage)
    sessionStorage.setItem('accountSwitching', 'true');

    // Switch to individual mode
    switchToIndividualMode();

    // Redirect to home page to refresh sidebar and data in individual context
    window.location.href = '/home';
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner':
        return '#0066cc'; // Blue
      case 'admin':
        return '#9c27b0'; // Purple
      case 'manager':
        return '#ff6f00'; // Orange
      case 'pilot':
        return '#008000'; // Green
      case 'viewer':
        return '#666666'; // Gray
      default:
        return '#666666';
    }
  };

  const getRoleLabel = (role) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  if (loading) {
    return (
      <Box className={styles['selector-loading']}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  // If user has no organizations and no individual subscription, don't show selector
  if (!isOrganizationUser && !hasIndividualSubscription) {
    return null;
  }

  // If user only has individual subscription (no organizations), show "Individual" indicator
  if (!isOrganizationUser) {
    return (
      <div className={`${styles['selector-individual']} ${collapsed ? styles['selector-individual-collapsed'] : ''}`}>
        <Person className={styles['individual-icon']} aria-hidden="true" />
        {!collapsed && <span className={styles['individual-label']}>Individual</span>}
      </div>
    );
  }

  const displayName = currentOrganization?.organizationName || 'Individual Account';
  const currentRole = currentOrganization?.userRole;

  return (
    <div className={styles['organization-selector']}>
      <Button
        id="organization-menu-button"
        aria-controls={open ? 'organization-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        className={`${styles['selector-button']} ${collapsed ? styles['selector-collapsed'] : ''}`}
        startIcon={<Business />}
        endIcon={!collapsed ? <ExpandMore /> : null}
      >
        {!collapsed && (
          <div className={styles['button-content']}>
            <span className={styles['org-name']}>{displayName}</span>
            {currentRole && (
              <Chip
                label={getRoleLabel(currentRole)}
                size="small"
                className={styles['role-chip']}
                style={{
                  backgroundColor: `${getRoleBadgeColor(currentRole)}15`,
                  color: getRoleBadgeColor(currentRole)
                }}
              />
            )}
          </div>
        )}
      </Button>

      <Menu
        id="organization-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'organization-menu-button',
          role: 'menu'
        }}
        className={styles['selector-menu']}
        PaperProps={{
          className: styles['menu-paper']
        }}
      >
        <div className={styles['menu-header']}>
          <span className={styles['menu-title']}>Switch Organization</span>
        </div>

        {/* Organization List */}
        {organizations.map((org) => {
          const isSelected = org.organizationUuid === currentOrganization?.organizationUuid;
          return (
            <MenuItem
              key={org.organizationUuid}
              onClick={() => handleOrganizationSelect(org)}
              selected={isSelected}
              className={styles['menu-item']}
              role="menuitemradio"
              aria-checked={isSelected}
            >
              <ListItemIcon className={styles['item-icon']}>
                {isSelected ? <Check /> : <Business />}
              </ListItemIcon>
              <ListItemText
                primary={
                  <div className={styles['org-item-content']}>
                    <span className={styles['org-item-name']}>{org.organizationName}</span>
                    <Chip
                      label={getRoleLabel(org.userRole)}
                      size="small"
                      className={styles['role-chip-small']}
                      style={{
                        backgroundColor: `${getRoleBadgeColor(org.userRole)}15`,
                        color: getRoleBadgeColor(org.userRole)
                      }}
                    />
                  </div>
                }
                secondary={org.organizationIdentifier || org.organizationUuid}
                classes={{
                  primary: styles['item-primary'],
                  secondary: styles['item-secondary']
                }}
              />
            </MenuItem>
          );
        })}

        {/* Individual Mode Option - always show to allow switching and subscribing */}
        <Divider className={styles['menu-divider']} />
        <MenuItem
          onClick={handleViewIndividual}
          className={styles['menu-item']}
          role="menuitem"
        >
          <ListItemIcon className={styles['item-icon']}>
            <Person />
          </ListItemIcon>
          <ListItemText
            primary="Individual Account"
            secondary={hasIndividualSubscription ? (individualSubscription?.planName || 'Personal Plan') : 'No subscription'}
            classes={{
              primary: styles['item-primary'],
              secondary: styles['item-secondary']
            }}
          />
        </MenuItem>

        {/* Create New Organization - Enterprise feature only */}
        {hasEnterpriseSubscription && (
          <>
            <Divider className={styles['menu-divider']} />
            <MenuItem
              onClick={handleCreateNew}
              className={styles['menu-item-action']}
              role="menuitem"
            >
              <ListItemIcon className={styles['item-icon']}>
                <Add />
              </ListItemIcon>
              <ListItemText
                primary="Create New Organization"
                classes={{
                  primary: styles['item-primary-action']
                }}
              />
            </MenuItem>
          </>
        )}
      </Menu>
    </div>
  );
};

export default OrganizationSelector;
